import { graphql } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import { cn, getSettingsClass } from '~/utils/helpers';

interface CalculatorProps {
  block: {
    settings?: Settings;
    cpu_per_hour_price: number;
    cpu_time_ratio: number;
    memory_price: number;
    storage_price: number;
    persistent_volume_price: number;
    field_labels: {
      instance_count_title: string;
      instance_count_description: string;
      cpu_cores_title: string;
      cpu_cores_description: string;
      average_cpu_usage_title: string;
      average_cpu_usage_description: string;
      memory_title: string;
      memory_description: string;
      nvme_storage_title: string;
      nvme_storage_description: string;
      persistent_volume_title: string;
      persistent_volume_description: string;
      persistent_volume_size_title: string;
      persistent_volume_size_description: string;
      memory_price_label: string;
      cpu_time_label: string;
      storage_price_label: string;
      persistent_volume_label: string;
      total_price_label: string;
    };
  };
  vars: any;
}

const CloudComputeCalculator: React.FC<CalculatorProps> = ({
  block: {
    settings,
    cpu_per_hour_price,
    cpu_time_ratio,
    memory_price,
    storage_price,
    // persistent_volume_price,
    field_labels,
  },
  vars,
}) => {
  const settingsClass = getSettingsClass(settings);
  const [instance_count, set_instance_count] = useState(1);
  const [cpu_cores, set_cpu_cores] = useState(2);
  const [memory, set_memory] = useState(16);
  const [nvme_storage, set_nvme_storage] = useState(50);
  const [average_cpu_usage, set_average_cpu_usage] = useState(50);
  // const [persistent_volume, set_persistent_volume] = useState(1);
  // const [persistent_volume_size, set_persistent_volume_size] = useState(50);
  const [price, set_price] = useState(0);
  const [cost_per_hour, set_cost_per_hour] = useState(0);
  const hours_per_month = 720; // Fixed value for hours in a Month

  const [rangeValue_EuNa_Gb, setRangeValueState_EuNa] = useState(200);
  const [rangeValue_AsOc_Gb, setRangeValueState_AsOc] = useState(50);
  const [rangeValue_Sa_Gb, setRangeValueState_Sa] = useState(0);
  const [rangeValue_MeAf_Gb, setRangeValueState_MeAf] = useState(0);
  const [cdn_traffic_price, set_cdn_traffic_price] = useState(0);

  const pricing_standard_eu_na_gb = Number(vars.pricing_standard_eu_na_gb);
  const pricing_standard_as_oc_gb = Number(vars.pricing_standard_as_oc_gb);
  const pricing_standard_sa_gb = Number(vars.pricing_standard_sa_gb);
  const pricing_standard_me_af_gb = Number(vars.pricing_standard_me_af_gb);

  const calculateTotalCost = useCallback(() => {
    const cpuCost =
      (average_cpu_usage / 100) *
      cpu_cores *
      hours_per_month *
      cpu_per_hour_price *
      cpu_time_ratio;
    const memoryCost = memory * memory_price * hours_per_month;
    const storageCost = nvme_storage * storage_price;
    // const persistentVolumeCost =
    //   persistent_volume_size * persistent_volume_price;
    const totalCost =
      instance_count * (cpuCost + memoryCost + storageCost) +
      // persistentVolumeCost +
      cdn_traffic_price;
    return Number.parseFloat(totalCost.toFixed(2));
  }, [
    instance_count,
    cpu_cores,
    memory,
    nvme_storage,
    average_cpu_usage,
    // persistent_volume,
    // persistent_volume_size,
    cdn_traffic_price,
  ]);

  function calculateStandardPrice() {
    let standardPrice = 0;
    standardPrice =
      rangeValue_EuNa_Gb * pricing_standard_eu_na_gb +
      rangeValue_AsOc_Gb * pricing_standard_as_oc_gb +
      rangeValue_Sa_Gb * pricing_standard_sa_gb +
      rangeValue_MeAf_Gb * pricing_standard_me_af_gb;

    return Math.round(standardPrice * 100) / 100;
  }

  useEffect(() => {
    set_cdn_traffic_price(calculateStandardPrice());
  }, [
    rangeValue_AsOc_Gb,
    rangeValue_EuNa_Gb,
    rangeValue_MeAf_Gb,
    rangeValue_Sa_Gb,
  ]);

  useEffect(() => {
    const totalCost = calculateTotalCost();
    set_price(totalCost);
    set_cost_per_hour(
      Number.parseFloat((totalCost / hours_per_month).toFixed(5))
    );
  }, [
    instance_count,
    cpu_cores,
    memory,
    nvme_storage,
    average_cpu_usage,
    // persistent_volume,
    // persistent_volume_size,
    cdn_traffic_price,
  ]);

  // useEffect(() => {
  //   // Reset persistent volume size if persistent volume count is 0
  //   if (persistent_volume * 100 < persistent_volume_size) {
  //     set_persistent_volume_size(persistent_volume * 100);
  //   }
  // }, [persistent_volume]);

  return (
    <div className={`flex h-fit w-full justify-center md:p-4 ${settingsClass}`}>
      <div
        className={`flex max-w-xl justify-center xl:max-w-lg lg:max-w-md md:max-w-sm`}
      >
        <div className='grid min-w-full grid-cols-2 bg-[#fff] pt-2 leading-snug text-text-primary box-shadow-light border-rounded-strong'>
          {/* div that spans 2 grid columns */}
          <div className='col-span-2 flex justify-between px-10 py-8 md:flex-col md:gap-6'>
            <div className='flex flex-col items-start'>
              <h4 className='text-2xl mb-0 font-bold'>
                {field_labels.instance_count_title}
              </h4>
              <p className='text-[16px] text-text-gray'>
                {field_labels.instance_count_description}
              </p>
            </div>
            <InputWithButtons
              label='Instance Count'
              value={instance_count}
              className='w-[220px] md:w-full'
              setValue={set_instance_count}
            />
          </div>
          <div className='flex flex-col border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <div className='mb-6'>
              <h4 className='text-2xl mb-0 font-bold'>
                {field_labels.cpu_cores_title}
              </h4>
              <p className='text-[16px] text-text-gray'>
                {field_labels.cpu_cores_description}
              </p>
            </div>
            <InputWithButtons
              label='CPU Cores'
              value={cpu_cores}
              setValue={set_cpu_cores}
            />
          </div>
          <div className='border-l border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <div className='mb-6'>
              <h4 className='text-2xl mb-0 font-bold'>
                {field_labels.average_cpu_usage_title}
              </h4>
              <p className='text-[16px] text-text-gray'>
                {field_labels.average_cpu_usage_description}
              </p>
            </div>
            <InputWithSlider
              label='Average CPU usage'
              value={average_cpu_usage}
              className='w-full'
              setValue={set_average_cpu_usage}
              unit='%'
            />
          </div>
          <div className='border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <div className='mb-6'>
              <h4 className='text-2xl mb-0 font-bold'>
                {field_labels.memory_title}
              </h4>
              <p className='text-[16px] text-text-gray'>
                {field_labels.memory_description}
              </p>
            </div>
            <InputWithSlider
              label='Memory'
              value={memory}
              setValue={set_memory}
              unit='GB'
              step={0.1}
              decimals={1}
              min={0.1}
              max={32}
            />
          </div>
          <div className='border-l border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <h4 className='text-2xl mb-0 font-bold'>
              {field_labels.nvme_storage_title}
            </h4>
            <p className='mb-6 text-[16px] text-text-gray'>
              {field_labels.nvme_storage_description}
            </p>
            <InputWithSlider
              label='NVMe Storage'
              value={nvme_storage}
              setValue={set_nvme_storage}
              unit='GB'
            />
          </div>
          {/* <div className='flex flex-col border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <div className='mb-6'>
              <h4 className='text-2xl mb-0 font-bold'>
                {field_labels.persistent_volume_title}
              </h4>
              <p className='text-[16px] text-text-gray'>
                {field_labels.persistent_volume_description}
              </p>
            </div>
            <InputWithButtons
              label='Persistent Volume'
              value={persistent_volume}
              setValue={set_persistent_volume}
              max={2}
            />
          </div> */}
          {/* <div className='border-l border-t border-solid border-gray-lighter px-10 py-8 md:col-span-2'>
            <h4 className='text-2xl mb-0 font-bold'>
              {field_labels.persistent_volume_size_title}
            </h4>
            <p className='mb-6 text-[16px] text-text-gray'>
              {field_labels.persistent_volume_size_description}
            </p>
            <InputWithSlider
              label='Persistent Volume Size'
              value={persistent_volume_size}
              setValue={set_persistent_volume_size}
              unit='GB'
              max={persistent_volume * 100}
            />
          </div> */}
          <div className='block__pricing_cdn col-span-2 border-l border-t border-solid border-gray-lighter !py-8 px-10'>
            <h4 className='text-2xl mb-0 font-bold'>
              Calculate your monthly traffic bill
            </h4>
            <p className='mb-6 text-[16px] text-text-gray'>
              Enter your monthly traffic to calculate estimated pricing.
            </p>
            <div className='pricing-main'>
              <div className='row'>
                <div className='col-md-6'>
                  <div className='card pricing-eu_na'>
                    <div className='top'>
                      <span className='title'>EU/NA</span>
                      <span className='size'>{rangeValue_EuNa_Gb} GB</span>
                    </div>
                    <div className='slidecontainer'>
                      <input
                        id='range-eu_na'
                        type='range'
                        defaultValue={200}
                        className='slider'
                        min='0'
                        max={1000}
                        onChange={event =>
                          setRangeValueState_EuNa(+event.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className='col-md-6'>
                  <div className='card pricing-as_oc'>
                    <div className='top'>
                      <span className='title'>Asia/Oceania</span>
                      <span className='size'>{rangeValue_AsOc_Gb} GB</span>
                    </div>
                    <div className='slidecontainer'>
                      <input
                        id='range-as_oc'
                        type='range'
                        defaultValue={50}
                        className='slider'
                        min='0'
                        max={1000}
                        onChange={event =>
                          setRangeValueState_AsOc(+event.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className='col-md-6'>
                  <div className='card pricing-sa'>
                    <div className='top'>
                      <span className='title'>South America</span>{' '}
                      <span className='size'>{rangeValue_Sa_Gb} GB</span>
                    </div>
                    <div className='slidecontainer'>
                      <input
                        id='range-sa'
                        type='range'
                        defaultValue={0}
                        className='slider'
                        min='0'
                        max={1000}
                        onChange={event =>
                          setRangeValueState_Sa(+event.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className='col-md-6'>
                  <div className='card pricing-me_af'>
                    <div className='top'>
                      <span className='title'>ME & Africa</span>{' '}
                      <span className='size'>{rangeValue_MeAf_Gb} GB</span>
                    </div>
                    <div className='slidecontainer'>
                      <input
                        id='range-me_af'
                        type='range'
                        defaultValue={0}
                        className='slider'
                        min='0'
                        max={1000}
                        onChange={event =>
                          setRangeValueState_MeAf(+event.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-span-2 flex items-center justify-between border-t border-solid border-gray-lighter px-10 py-8 md:items-start'>
            <h4 className='mb-0 text-[2.0rem] md:leading-loose'>
              {field_labels.memory_price_label}
            </h4>
            <div className='flex items-baseline gap-2 text-menu-primary md:flex-col md:items-end md:gap-0'>
              <span className='text-[3.5rem] font-medium'>${memory_price}</span>
              <span className='text-[1.5rem]'>/GB per hour</span>
            </div>
          </div>
          <div className='col-span-2 flex items-center justify-between border-t border-solid border-gray-lighter px-10 py-8 md:items-start'>
            <h4 className='mb-0 text-[2.0rem] md:leading-loose'>
              {field_labels.cpu_time_label}
            </h4>
            <div className='flex items-baseline gap-2 text-menu-primary md:flex-col md:items-end md:gap-0'>
              <span className='text-[3.5rem] font-medium'>
                ${cpu_per_hour_price}
              </span>
              <span className='text-[1.5rem]'>/per hour</span>
            </div>
          </div>
          <div className='col-span-2 flex items-center justify-between border-t border-solid border-gray-lighter px-10 py-8 md:items-start'>
            <h4 className='mb-0 text-[2.0rem] md:leading-loose'>
              {field_labels.storage_price_label}
            </h4>
            <div className='flex items-baseline gap-2 text-menu-primary md:flex-col md:items-end md:gap-0'>
              <span className='text-[3.5rem] font-medium'>
                ${storage_price.toFixed(2)}
              </span>
              <span className='text-[1.5rem]'>/GB per month</span>
            </div>
          </div>
          {/* <div className='col-span-2 flex items-center justify-between border-t border-solid border-gray-lighter px-10 py-8 md:items-start'>
            <h4 className='mb-0 text-[2.0rem] md:leading-loose'>
              {field_labels.persistent_volume_label}
            </h4>
            <div className='flex items-baseline gap-2 text-menu-primary md:flex-col md:items-end md:gap-0'>
              <span className='text-[3.5rem] font-medium'>
                ${persistent_volume_price.toFixed(2)}
              </span>
              <span className='text-[1.5rem]'>/GB per month</span>
            </div>
          </div> */}
          <div className='col-span-2 flex items-center justify-between border-t border-solid border-gray-lighter px-10 py-8 md:items-start'>
            <h4 className='mb-0 text-[2.0rem] md:leading-loose'>
              Traffic Usage
            </h4>
            <div className='flex items-baseline gap-2 text-menu-primary md:flex-col md:items-end md:gap-0'>
              <span className='text-[3.5rem] font-medium'>
                ${cdn_traffic_price.toFixed(2)}
              </span>
            </div>
          </div>
          <div className='col-span-2 flex justify-between gap-4 border-t border-solid border-gray-lighter px-10 py-8 sm:flex-col sm:items-start sm:justify-start sm:gap-6'>
            <div className='flex flex-col'>
              <h4 className='mb-0 text-[2.4rem]'>
                {field_labels.total_price_label}
              </h4>
              <div>
                <span>Cost per hour: </span>
                <span className='text-orange'>${cost_per_hour}</span>
              </div>
            </div>
            <h2 className='text-orange mb-0 flex-1 text-right text-[5.5rem]'>
              ${price.toLocaleString('en-US')}
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
};

const numericClickHandler =
  (setValue: (value: number) => void, min: number, max: number) =>
  (newValue: number) =>
  (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (newValue >= min) {
      setValue(newValue);
    }
    if (newValue > max) {
      setValue(max);
    }
  };

const numericChangeHandler =
  (
    setValue: (value: number) => void,
    min: number,
    max: number,
    decimals?: number
  ) =>
  (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    let outputValue = Number(event.target.value);

    if (inputValue.startsWith('0')) {
      outputValue = Number(inputValue.replace(/^0+/, ''));
      setValue(+outputValue.toFixed(decimals));
    }
    if (outputValue >= min - 1 && outputValue <= max) {
      setValue(+outputValue.toFixed(decimals));
    }
    if (outputValue > max) {
      setValue(max);
    }
    if (isNaN(outputValue) || outputValue < min) {
      setValue(min);
    }
  };

const numericBlurHandler =
  (setValue: (value: number) => void, min: number, max: number) =>
  (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    const outputValue = Number(event.target.value);
    if (isNaN(outputValue)) {
      setValue(min);
    }
    if (outputValue < min) {
      setValue(min);
    }
    if (outputValue > max) {
      setValue(max);
    }
  };

/** A component with input at the top and range slider at the bottom */
const InputWithSlider = ({
  label,
  value,
  setValue,
  className,
  unit = 'GB',
  decimals = 0,
  step = 1,
  min = 1,
  max = 100,
}: {
  label: string;
  value: number;
  setValue: (value: number) => void;
  className?: string;
  decimals?: number;
  step?: number;
  unit?: string;
  min?: number;
  max?: number;
}) => {
  const handleChange = useCallback(
    numericChangeHandler(setValue, min, max, decimals),
    [setValue, min, max]
  );
  const resetOnBlur = useCallback(numericBlurHandler(setValue, min, max), [
    setValue,
    min,
    max,
  ]);

  return (
    <div className={cn(`flex flex-col gap-8 ${className}`)}>
      <div className='relative flex h-[50px] items-center justify-center'>
        <input
          type='text'
          value={value}
          onChange={handleChange}
          onBlur={resetOnBlur}
          className='h-full w-full border border-gray-lighter px-3 py-4 text-center text-[3rem] font-bold text-text-primary [appearance:textfield] focus:outline-blue-border [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
        />
        <div
          attr-unit={unit}
          className='pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 touch-none select-none text-[3rem] font-bold text-text-primary'
        >
          <span className='pl-2 opacity-0'>
            {/** 2. Resets unit width - will result in aligned value */}
            {unit}
          </span>
          <span>{value}</span>
          <span className='text-transparent pl-2'>
            {/** 1. CSS will count this into element's width - value will not be aligned */}
            {unit}
          </span>
        </div>
      </div>
      <input
        type='range'
        value={value}
        onChange={e => setValue(Number(e.target.value))}
        className='slider w-full'
        step={step}
        min={min}
        max={max}
      />
    </div>
  );
};

/** A component with minus button on the left, input field in the middle and plus button on the right */
const InputWithButtons = ({
  label,
  value,
  setValue,
  className,
  min = 1,
  max = 50,
}: {
  label: string;
  value: number;
  setValue: (value: number) => void;
  className?: string;
  min?: number;
  max?: number;
}) => {
  const handleClick = useCallback(numericClickHandler(setValue, min, max), [
    setValue,
    min,
    max,
  ]);
  const handleChange = useCallback(numericChangeHandler(setValue, min, max), [
    setValue,
    min,
    max,
  ]);
  const resetOnBlur = useCallback(numericBlurHandler(setValue, min, max), [
    setValue,
    min,
    max,
  ]);

  return (
    <div
      className={cn(
        `flex shrink-0 items-center justify-start gap-0 ${className}`
      )}
    >
      <button
        className='flex h-[50px] w-[50px] items-center justify-center rounded-l-[10px] border border-r-0 border-gray-lighter p-0 active:bg-gray-transparent'
        onClick={handleClick(value - 1)}
        type='button'
      >
        <span className='fas fa-minus text-[2.2rem] font-bold leading-none text-orange-gradient'></span>
      </button>
      <input
        type='text'
        value={value}
        onChange={handleChange}
        onBlur={resetOnBlur}
        className='h-[50px] w-full flex-1 border border-gray-lighter px-3 py-4 text-center text-[3rem] font-bold [appearance:textfield] focus:outline-blue-border [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
        min={min}
        max={max}
        inputMode='numeric'
        step={1}
      />
      <button
        className='flex h-[50px] w-[50px] items-center justify-center rounded-r-[10px] border border-l-0 border-gray-lighter p-0 active:bg-gray-transparent'
        onClick={handleClick(value + 1)}
        type='button'
      >
        <span className='fas fa-solid fa-plus text-[2.6rem] leading-none text-orange-gradient'></span>
      </button>
    </div>
  );
};

export default CloudComputeCalculator;

export const cloudComputeCalculatorFragment = graphql`
  fragment CalculatorSettingsFragment on Block {
    settings {
      ...SettingsFragment
    }
    cpu_per_hour_price
    cpu_time_ratio
    memory_price
    storage_price
    persistent_volume_price
    field_labels {
      instance_count_title
      instance_count_description
      cpu_cores_title
      cpu_cores_description
      average_cpu_usage_title
      average_cpu_usage_description
      memory_title
      memory_description
      nvme_storage_title
      nvme_storage_description
      persistent_volume_title
      persistent_volume_description
      persistent_volume_size_title
      persistent_volume_size_description
      memory_price_label
      cpu_time_label
      storage_price_label
      persistent_volume_label
      total_price_label
    }
  }
`;
